import { makeObservable, makeAutoObservable, action, observable } from "mobx";
//import { observer } from "mobx-react";

import { RetrieveFromApi } from "../util/RetrieveFromApi";

import { AccountApi } from "../util/api/AccountApi";
import { EmployerApi } from "../util/api/EmployerApi";
import { StudentApi } from "../util/api/StudentApi";
import { SupervisorApi } from "../util/api/SupervisorApi";
import { TimesheetApi } from "../util/api/TimesheetApi";
import { TimestampApi } from "../util/api/TimestampApi";
import { MessageApi } from "../util/api/MessageApi";
import { HolidayApi } from "../util/api/HolidayApi";
import { ValidTimeslotsApi } from "../util/api/ValidTimeslotsApi";
import { AppUserApi } from '../util/api/ServiceObjects/AppUserApi';


//Wrapper objects provide us some abstraction + ability to extend

import Student from "./wrappers/Student";
import Account from "./wrappers/Account";
import Supervisor from "./wrappers/Supervisor";
import Employer from "./wrappers/Employer";
import Timesheet from "./wrappers/Timesheet";
import Timestamp from "./wrappers/Timestamp";
import Message from "./wrappers/Message";
import Holiday from "./wrappers/Holiday";

import AppUser from "./wrappers/ServiceObjects/AppUser";

import { ApiObjectCollection } from "./collections/ApiObjectCollection";

import _ from 'lodash';
import axios from "axios";
import Cookie from "js-cookie";

require('dotenv');


class Store {

    constructor() {

        const {
            bannerText,
            bannerSubtitle,
            isFlexLayout
        } = this.getBannerCookie();
        this.bannerText = bannerText;
        this.bannerSubtitle = bannerSubtitle;
        this.isFlexLayout = isFlexLayout;

        this.loading = true;
        this.user = {};
        this.prevUser = {};
        this.state = "unloaded";
        this.alerts = [];

        this.accounts = [];
        this.supervisors = [];
        this.employers = [];
        this.students = [];
        this.timesheets = [];
        this.timestamps = [];
        this.messages = [];
        this.holidays = [];
        this.validTimeslots = [];

        makeObservable(this, {
            bannerText: observable,
            bannerSubtitle: observable,
            isFlexLayout: observable,

            loading: observable,
            user: observable,
            prevUser: observable,
            state: observable,
            alerts: observable,

            accounts: observable,
            supervisors: observable,
            employers: observable,
            students: observable,
            timesheets: observable,
            timestamps: observable,
            messages: observable,
            holidays: observable,
            validTimeslots: observable,

            getFromApi: action,
            attemptAuthentication: action,
            addAlert: action,
            clearAlerts: action,

            addItem: action,
            updateBanner: action,
            changeView: action,
            getUserCookie: action,
            setUserCookie: action,
            setLoading: action
        }, { autoBind: true });
    }

    getUserCookie() {
        let user = Cookie.getJSON('user');

        if (user) {
            this.user = user;
            this.state = "authenticated";

            let prevUser = Cookie.getJSON('prevUser');
            console.log(prevUser)
            if (prevUser) {
                this.prevUser = prevUser;
            }

            return true;
        }
        return false;
    }

    setUserCookie() {
        Cookie.set('user', this.user);

        if (!_.isEmpty(this.prevUser)) {
            Cookie.set('prevUser', this.prevUser);
        }
        else {
            Cookie.remove("prevUser");
        }
    }

    //This attempts to auto-authorize the user; it will fail if they haven't logged in recently. 
    async attemptAuthentication() {
        //Don't fetch if we have already authenticated.
        if (!_.isEmpty(this.user) || this.getUserCookie()) {
            this.state = "authenticated";
            return true;
        }

        try {
            //Fetch data from API
            const user = await AppUserApi.get();
            //user.isAdmin = false;
            this.user = new AppUser(user);

            this.setUserCookie();

            this.state = "authenticated";
            return true;
        }
        catch (e) {
            console.log(e);
            //This just means the user couldn't be auto-authenticated, not an actual error.
            return false;
        }
    }

    async getFromApi(include = [], hardRefresh = true) {
        let authSuccess = true;
        //If the user hasn't be retrieved yet, retrieve them.
        if (_.isEmpty(this.user)) {
            authSuccess = await this.attemptAuthentication();
        }

        if (authSuccess) {
            this.loading = true;

            try {
                const requests = [];

                let queryEndpoint = null;
                queryEndpoint = (apiObject) => {
                    return axios.get(process.env.REACT_APP_API_ENDPOINT + apiObject.endpoint, { withCredentials: true });
                }


                if (include.length == 0 || include.includes("accounts")) {
                    const Accounts = queryEndpoint(AccountApi);
                    requests.push(Accounts);

                    Accounts.then(AccountsResponse => {
                        let accounts = AccountsResponse.data.map((account) => new Account(account, this)); //Give account some helper functions & observability
                        this.accounts = new ApiObjectCollection(accounts); //Give the collection some helper functions
                    });
                }

                if (include.length == 0 || include.includes("supervisors")) {
                    const Supervisors = queryEndpoint(SupervisorApi);
                    requests.push(Supervisors);

                    Supervisors.then(SupervisorsResponse => {
                        let supervisors = SupervisorsResponse.data.map((supervisor) => new Supervisor(supervisor));
                        this.supervisors = new ApiObjectCollection(supervisors);
                    });
                }

                if (include.length == 0 || include.includes("employers")) {
                    const Employers = queryEndpoint(EmployerApi)
                    requests.push(Employers);

                    Employers.then(EmployersResponse => {
                        let employers = EmployersResponse.data.map((employer) => new Employer(employer));
                        this.employers = new ApiObjectCollection(employers);
                    });
                }

                if (include.length == 0 || include.includes("students")) {
                    const Students = queryEndpoint(StudentApi);
                    requests.push(Students);

                    Students.then(StudentsResponse => {
                        let students = StudentsResponse.data.map((student) => new Student(student));
                        this.students = new ApiObjectCollection(students);

                        //console.log("students in store: ", this.students.collection)
                        this.students.sortByLastName = (a, b) => {
                            if (a.lastName.toLowerCase() > b.lastName.toLowerCase()) {
                                return 1;
                            }
                            else if (b.lastName.toLowerCase() > a.lastName.toLowerCase()) {
                                return -1
                            }
                            else {
                                if (a.firstName.toLowerCase() > b.firstName.toLowerCase()) {
                                    return 1;
                                }
                                else if (b.firstName.toLowerCase() > a.firstName.toLowerCase()) {
                                    return -1
                                }
                                return 0;
                            }
                        };
                    });
                }

                if (include.length == 0 || include.includes("timesheets")) {
                    const Timesheets = queryEndpoint(TimesheetApi);
                    requests.push(Timesheets);

                    Timesheets.then(TimesheetsResponse => {
                        let timesheets = TimesheetsResponse.data.map(timesheet => new Timesheet(timesheet));
                        this.timesheets = new ApiObjectCollection(timesheets);
                    });
                }

                if (include.length == 0 || include.includes("timestamps")) {
                    const Timestamps = queryEndpoint(TimestampApi);
                    requests.push(Timestamps);

                    Timestamps.then(TimestampResponse => {
                        let timestamps = TimestampResponse.data.map(timestamp => new Timestamp(timestamp));
                        this.timestamps = new ApiObjectCollection(timestamps);
                    });
                }

                this.messages = new ApiObjectCollection([]);
                if (include.includes("messages")) {
                    const Messages = queryEndpoint(MessageApi)
                    requests.push(Messages);

                    Messages.then(MessagesResponse => {
                        let messages = MessagesResponse.data.map(message => new Message(message));
                        this.messages = new ApiObjectCollection(messages);
                    });
                }

                if (include.length == 0 || include.includes("holidays")) {
                    const Holidays = queryEndpoint(HolidayApi);
                    requests.push(Holidays);

                    Holidays.then(HolidaysResponse => {
                        let holidays = HolidaysResponse.data.map(holiday => new Holiday(holiday));
                        this.holidays = new ApiObjectCollection(holidays);
                    });
                }

                if (include.length == 0 || include.includes("timeslots")) {
                    const ValidTimeslots = RetrieveFromApi.getItems(ValidTimeslotsApi)
                    requests.push(ValidTimeslots);

                    ValidTimeslots.then(validTimeslots => {
                        this.validTimeslots = validTimeslots;
                    })
                }

                return axios.all(requests).then(() => {
                    this.loading = false;
                    this.state = "loaded";
                });

            }
            catch (e) {
                console.log(e);
                this.state = "error";

                this.addAlert("An error occurred while fetching data. Please try refreshing the page, and contact an adminstrator if the problem persists.");
            }
        }
    }

    addAlert(alert) {
        this.alerts.push(alert);
    }

    clearAlerts() {
        this.alerts = [];
    }

    addItem(item) {

        switch (item.type) {
            case "timesheet":
                //Push the timesheet to the timesheet array 
                this.timesheets.collection.push(item.data);

                //Modify the account so it contains the new timesheet id.
                let account = this.accounts.getById(item.data.accountId)
                account.timesheetIds.push(item.data.id);

                break;
            case "account":
                this.accounts.collection.push(item.data);
                break;
            case "employer":
                this.employers.collection.push(item.data);
                break
            case "supervisor":
                this.supervisors.collection.push(item.data);

                //Modify the supervisor's employer so it contains the new timesheet id.
                let supervisorEmployer = this.employers.getById(item.data.employerId);

                if (supervisorEmployer) {
                    supervisorEmployer.supervisorIds.push(item.data.id);
                }
                break
            case "student":
                this.students.collection.push(item.data);


                /*Modify the account so it contains the new student id.
                let account = this.accounts.getById(item.data.accountId)
                if (account) {
                    account.studentIds.push(item.data.id);
                }*/

                break
            case "accountStatusUpdate":
                console.log(item);

                //Modify the account so it contains the new account status.  
                let accountStatusAccount = this.accounts.getById(item.data.accountId)
                console.log(accountStatusAccount)
                if (accountStatusAccount) {

                    accountStatusAccount.accountStatusChanges.push(item.data.update);
                }
                break;
        }
    }

    setBannerCookie() {
        const banner = {
            bannerText: this.bannerText,
            bannerSubtitle: this.bannerSubtitle,
            isFlexLayout: this.isFlexLayout
        }

        Cookie.set('bannerInfo', banner);
    }

    getBannerCookie() {
        const bannerInfo = Cookie.getJSON("bannerInfo");

        return {
            bannerText: bannerInfo ? bannerInfo.bannerText : "Welcome!",
            bannerSubtitle: bannerInfo ? bannerInfo.bannerSubtitle : false,
            isFlexLayout: bannerInfo ? bannerInfo.isFlexLayout : false
        }
    }

    updateBanner(text, subtitle = false, isFlexLayout = false) {
        this.bannerText = text;
        this.bannerSubtitle = subtitle;
        this.isFlexLayout = isFlexLayout;

        this.setBannerCookie()
    }

    async changeView(studentId) {

        console.log(this.user)
        console.log(this.prevUser)

        if (Object.keys(this.prevUser).length !== 0) {
            console.log(this.prevUser)

            this.user = Object.assign({}, this.prevUser);
            this.prevUser = {}
        }
        else {
            const student = this.students.getById(studentId);

            this.prevUser = Object.assign({}, this.user);


            this.user = {
                isAdmin: false,
                studentId: studentId,
                accountIds: student.accountIds,
                studentName: student.name,
                userName: student.tuid,
                isUser: true
            }

        }

        this.setUserCookie();

        return
    }

    signOut() {
        Cookie.remove("bannerInfo");
        Cookie.remove("user");
        Cookie.remove("prevUser");

        window.location = process.env.REACT_APP_API_LOGOUT
    }

    setLoading(bool) {
        this.loading = bool === true;
    }

    /*async fetchProjects() {
        this.state = "pending"

        try {
            const projects = await fetchGithubProjectsSomehow()
            const filteredProjects = somePreprocessing(projects)
            runInAction(() => {
                this.githubProjects = filteredProjects
                this.state = "done"
            })
        } catch (e) {
            runInAction(() => {
                this.state = "error"
            })
        }
    }*/

}


let store = new Store();

store.getFromApi();
//store.getFromApi(["timeslots"]);

export default store;